import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { isEmpty, noop } from 'lodash'
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd'
import {
  NAMESPACE,
  TableColumnChooserProps,
  TableColumnType,
} from '../../utils'
import useOutsideClick from '../hooks/useOutsideHook'
import { SvgColumn, SvgDrag } from '../../assets/images'
import CustomButton from '../CustomButton'

const TableColumnChooser = <T,>({
  currentCol = [],
  onColumnListUpdate = noop,
  restoreDefault,
  showRestoreButton = false,
}: TableColumnChooserProps<T>) => {
  const [moreColumns, setMoreColumns] = useState<boolean>(false)
  const [checkedCol, setCheckedCol] = useState<TableColumnType<T>[]>([])
  const containerRef = useRef<HTMLDivElement | null>(null)
  const positionRef = useRef<{
    top: string
    right: string
  }>({
    top: '0px',
    right: '0px',
  })

  const handleCancel = () => {
    setCheckedCol(currentCol)
    setMoreColumns(false)
  }
  useOutsideClick({
    ref: containerRef,
    handler: handleCancel,
  })

  useEffect(() => {
    setCheckedCol(currentCol)
  }, [currentCol])

  const handleCheckedClicked = (index: number) => {
    if (checkedCol[index]?.isMandatory) return
    setCheckedCol((prev) => {
      const newState = [...prev]
      const element = { ...newState[index] }
      element.isHidden = !element.isHidden
      newState[index] = element
      return newState
    })
  }

  const handleAdd = () => {
    onColumnListUpdate([...checkedCol])
    setMoreColumns(false)
  }

  const handleOnDragEnd = (result: DropResult) => {
    if (!result.destination) return

    const movedColumn = checkedCol?.[result?.source?.index]
    if (isEmpty(movedColumn)) {
      console.error('Moved column not found')
      return
    }
    const swappedColumns = swapElements(
      checkedCol,
      result?.destination?.index,
      result?.source?.index,
      movedColumn
    )

    setCheckedCol([...swappedColumns])
  }
  const swapElements = <T,>(
    elements: TableColumnType<T>[],
    destinationIndex: number,
    sourceIndex: number,
    sourceElement: TableColumnType<T>
  ) => {
    elements = [
      ...elements.slice(0, sourceIndex),
      ...elements.slice(sourceIndex + 1),
    ]
    elements.splice(destinationIndex, 0, sourceElement)

    return elements
  }

  return (
    <div className="relative w-full">
      <button
        className="align-middle"
        onClick={(e) => {
          positionRef.current!.top = `${
            e.currentTarget.getBoundingClientRect().y + 8
          }px`
          positionRef.current!.right = `${
            window.innerWidth - e.currentTarget.getBoundingClientRect().right
          }px`
          setMoreColumns((prev) => !prev)
        }}
      >
        <span>
          <SvgColumn />
        </span>
      </button>
      {moreColumns && (
        <div
          ref={containerRef}
          className={`flex flex-col fixed top-[${positionRef.current.top}] right-[${positionRef.current.right}] -translate-x-full bg-accent-0  z-50 border-boxColour border rounded-lg`}
        >
          {showRestoreButton && (
            <div>
              <div className="flex gap-12 justify-between p-3">
                <span className="font-primary text-xs font-normal truncate">
                  {NAMESPACE.ADD_COLUMN}
                </span>
                <button
                  className="text-secondary-500 font-primary text-xs font-light truncate cursor-pointer"
                  onClick={() => {
                    if (restoreDefault) {
                      restoreDefault()
                      setMoreColumns(false)
                    }
                  }}
                >
                  {NAMESPACE.RESTORE_DEFAULT}
                </button>
              </div>
              <hr />
            </div>
          )}
          <div className="h-[11rem] overflow-y-auto">
            <DragDropContext onDragEnd={handleOnDragEnd}>
              <Droppable droppableId="column-list">
                {(provided) => (
                  <ul
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={{ listStyle: 'none', padding: 0 }}
                  >
                    {checkedCol?.map((col, index) =>
                      col.showInColumnChooser === false ? null : (
                        <Draggable
                          key={`${col?.header},${index}`}
                          draggableId={`${col?.header},${index}`}
                          index={index}
                        >
                          {(provided, snapshot) => {
                            const child = (
                              <li
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className="flex items-center p-1 pl-4 bg-accent-0 justify-between rounded-lg"
                                style={{
                                  ...provided.draggableProps.style,
                                }}
                              >
                                <label className="flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={!col?.isHidden || col?.isMandatory}
                                    disabled={col?.isMandatory}
                                    onChange={() => handleCheckedClicked(index)}
                                    className="mr-2"
                                  />
                                  <span className="font-normal whitespace-nowrap">
                                    {col.header}
                                  </span>
                                </label>
                                <div
                                  {...provided.dragHandleProps}
                                  className=" cursor-grab p-2 mr-1"
                                >
                                  <SvgDrag />
                                </div>
                              </li>
                            )

                            return snapshot.isDragging
                              ? ReactDOM.createPortal(child, document.body)
                              : child
                          }}
                        </Draggable>
                      )
                    )}
                    {provided.placeholder}
                  </ul>
                )}
              </Droppable>
            </DragDropContext>
          </div>
          <div className="btngrp flex gap-3 p-4">
            <CustomButton
              variant="secondary"
              size="small"
              width="8.2rem"
              onClick={handleCancel}
              widthClass=""
              className="flex-1"
            >
              {NAMESPACE?.CANCEL}
            </CustomButton>
            <CustomButton
              isFilled={true}
              size="small"
              width="8.2rem"
              type="submit"
              onClick={handleAdd}
              className="flex-1"
              widthClass=""
            >
              {NAMESPACE?.SAVE}
            </CustomButton>
          </div>
        </div>
      )}
    </div>
  )
}

export default TableColumnChooser
